import Cookies from 'js-cookie';
import { EFFECTIVE_COUNTRY_LIST, COOKIE_NAME } from './constant';
import { teaReport } from '$tea';
import {
  CookieParam,
  CookieConsent,
  CookieConsentConfig,
  AsyncTask,
  SingleConsentConfig,
  CookieConsentWithVersion,
  RawCookieConsentConfig,
} from '../interface/cookie-banner';

function getDomain() {
  return `.${window.location.hostname.split('.').slice(-2).join('.')}`;
}

function parseCookieCodes(cookieConfig: Omit<CookieConsentConfig, 'version'> | undefined): string[] {
  if (!cookieConfig) {
    return [];
  }
  return Object.keys(cookieConfig).filter((code) => code !== 'version');
}

function getDefaultConsents(cookieConfig: Omit<CookieConsentConfig, 'version'> | null): CookieConsent {
  if (!cookieConfig) {
    return {};
  }
  return Object.keys(cookieConfig)
    .filter((code) => code !== 'version')
    .reduce((obj: Record<string, boolean>, key: string) => {
      const singleCookieConfig = cookieConfig[key] as SingleConsentConfig;
      obj[key] = singleCookieConfig.default;
      return obj;
    }, {});
}

function getPureConsentObject(consentObj: CookieConsent | CookieConsentWithVersion): CookieConsent {
  return Object.keys(consentObj)
    .filter((code) => code !== 'version')
    .reduce((obj: Record<string, boolean>, key: string) => {
      obj[key] = consentObj[key] as boolean;
      return obj;
    }, {});
}

function getConsentVersion(params?: Partial<CookieParam>): string | null {
  const { cookie } = params || {};
  const consent = cookie ? decodeURIComponent(cookie) : Cookies.get(COOKIE_NAME);
  if (!consent) {
    return null;
  }
  try {
    const consentObj = consent && JSON.parse(consent);
    return consentObj.version || null;
  } catch (e) {
    return null;
  }
}

/**
 * If both currentVersion and localVersion is written in 'v%d' format, compare the numbers after letter 'v'.
 * If not, fallback to string compare.
 */
function isConsentVersionNewer(currentVersion: string, localVersion: string | null): boolean {
  if (!localVersion) {
    return true;
  }
  const versionExp = /^v(\d+)$/;
  const matchCurrentVersion = versionExp.exec(currentVersion);
  const matchLocalVersion = versionExp.exec(localVersion);
  if (matchCurrentVersion && matchLocalVersion) {
    return Number(matchCurrentVersion[1]) > Number(matchLocalVersion[1]);
  } else {
    return currentVersion !== localVersion;
  }
}

export function resembleCookieConfig(rawConfig: RawCookieConsentConfig): CookieConsentConfig {
  return rawConfig.data.reduce(
    (config, groupConfig) => ({
      ...config,
      ...groupConfig.items,
    }),
    {
      version: rawConfig.version,
    },
  );
}

class CookieConfigManager {
  isRequestInflight = false;
  value: CookieConsentConfig | undefined;
  rawValue: RawCookieConsentConfig | undefined;
  pendingTasks: AsyncTask<CookieConsentConfig>[] = [];

  getCookieConfig(): CookieConsentConfig | undefined {
    if (!this.value) {
      console.error('`CookieConfigManager.getCookieConfig` returned undefined.');
    }
    return this.value;
  }

  getCookieCodes(): string[] {
    return parseCookieCodes(this.value);
  }

  getRawCookieConfig(): RawCookieConsentConfig | undefined {
    if (!this.rawValue) {
      console.error('`CookieConfigManager.getRawCookieConfig` returned undefined.');
    }
    return this.rawValue;
  }

  getDefaultConsents(): CookieConsent {
    if (!this.value) {
      console.error('`CookieConfigManager.getDefaultConsents` returned undefined.');
      return {};
    }
    return getDefaultConsents(this.value);
  }

  getFullConsents(): CookieConsent {
    if (!this.value) {
      console.error('`CookieConfigManager.getFullConsents` returned undefined.');
      return {};
    }
    return parseCookieCodes(this.value).reduce((obj: Record<string, boolean>, key: string) => {
      obj[key] = true;
      return obj;
    }, {});
  }

  getEmptyConsents(): CookieConsent {
    if (!this.value) {
      return {};
    }
    return parseCookieCodes(this.value).reduce((obj: Record<string, boolean>, key: string) => {
      obj[key] = false;
      return obj;
    }, {});
  }

  setCookieConsents(value: CookieConsent): CookieConsent {
    if (!this.value) {
      return {};
    }
    const cookiesAwaitConsentCodes = parseCookieCodes(this.value);
    for (const key of cookiesAwaitConsentCodes) {
      if (!value[key]) {
        const { cookies = [] } = (this.value[key] as SingleConsentConfig) || {};
        cookies.forEach((cookie) =>
          Cookies.remove(cookie, {
            domain: getDomain(),
          }),
        );
      }
    }
    const consentCookieValue = JSON.stringify({ ...value, version: this.value.version });
    Cookies.set(COOKIE_NAME, consentCookieValue, {
      expires: 30 * 13,
      domain: getDomain(),
      secure: true,
      sameSite: 'None',
    });
    setTimeout(() => {
      if (Cookies.get(COOKIE_NAME) !== consentCookieValue) {
        teaReport('cookie_banner_save_failed');
      }
    }, 100);
    return value;
  }

  setFullConsents(): CookieConsent {
    return this.setCookieConsents(this.getFullConsents());
  }

  setEmptyConsents(): CookieConsent {
    return this.setCookieConsents(this.getEmptyConsents());
  }

  /*
   * { ga: boolean, af: boolean }
   **/
  getCookieConsents(params: CookieParam): CookieConsent {
    if (!this.value) {
      return {};
    }
    const { region, cookie, isFTC } = params;
    // const cookieConfig = cookiesAwaitConsent || DEPRECATED_cookieChoiceDefault;
    const cookieCodes = parseCookieCodes(this.value);
    const consent = cookie ? decodeURIComponent(cookie) : Cookies.get(COOKIE_NAME);
    const isEuroCountry = EFFECTIVE_COUNTRY_LIST.includes(region);
    const notEuroCountryConsents = cookieCodes.reduce((obj: Record<string, boolean>, key: string) => {
      obj[key] = true;
      return obj;
    }, {});

    try {
      const consentObj = consent && JSON.parse(consent);
      // Cannot use third-party cookies when is US FTC User
      if (isFTC) {
        return this.getEmptyConsents();
      }
      // have consent info stored in browser
      if (consentObj) {
        return getPureConsentObject(
          Object.assign({}, isEuroCountry ? getDefaultConsents(this.value) : notEuroCountryConsents, consentObj),
        );
      }
    } catch (e) {}

    // do not have consent info stored in browser
    if (!isEuroCountry) {
      return notEuroCountryConsents;
    }
    return getDefaultConsents(this.value);
  }

  hasAgreedToAll(params: CookieParam): boolean {
    const cookieConsent = this.getCookieConsents(params);
    return parseCookieCodes(this.value).every((cookieVendor) => cookieConsent[cookieVendor]);
  }

  isShowCookieBanner(params: CookieParam): boolean {
    const { region } = params;
    if (!EFFECTIVE_COUNTRY_LIST.includes(region)) {
      // in non-euro countries, we are allowed to use any third-party cookies without informing users
      return false;
    }
    if (!this.value) {
      return false;
    }
    const localVersion = getConsentVersion(params);
    return isConsentVersionNewer(this.value.version, localVersion);
  }

  setCookieConfig(rawConfig: RawCookieConsentConfig) {
    this.value = resembleCookieConfig(rawConfig);
    this.rawValue = rawConfig;
  }
}

const instance = new CookieConfigManager();

export default instance;

export type ICookieConfigManager = typeof instance;
